/**
 * PasskeyManager — card on /account for enrolling + managing passkeys.
 *
 * Signed-in users add Face ID / Touch ID / Windows Hello / a security
 * key to their account. Once enrolled, the passkey can sign them in
 * on any subsequent visit without a password.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ScanFace, Fingerprint, KeyRound, Trash2 } from "lucide-react";
import { PasskeyRegisterButton } from "@/components/auth/PasskeyButtons";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
function prettyDate(v) {
  try {
    return new Date(v).toLocaleDateString(undefined,
      { year: "numeric", month: "short", day: "numeric" });
  } catch { return "—"; }
}

export default function PasskeyManager() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [supported, setSupported] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/auth/passkey/list`,
        { withCredentials: true });
      setRows(data?.passkeys || []);
    } catch (_) { setRows([]); }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined" && window.PublicKeyCredential) {
      setSupported(true);
    }
    load();
  }, [load]);

  async function remove(credentialId) {
    if (!confirm("Remove this passkey? You won't be able to use it to sign in "
                  + "anymore. If it's the last device on your account, keep it.")) return;
    try {
      await axios.delete(`${API}/api/auth/passkey/${credentialId}`,
        { withCredentials: true });
      toast.success("Passkey removed.");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't remove that passkey.");
    }
  }

  if (!supported) {
    return (
      <div className="bg-white border border-[#E8E5DF] rounded-2xl p-6 md:p-8"
            data-testid="passkey-manager-unsupported">
        <div className="flex items-center gap-3 mb-3">
          <KeyRound size={20} className="text-[#5C625E]"/>
          <h2 className="font-serif-display text-2xl">Passkeys</h2>
        </div>
        <p className="text-sm text-[#5C625E]">
          Your browser doesn&apos;t support passkeys yet. Try the latest
          Chrome, Safari or Edge to enrol Face ID / Touch ID.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-[#E8E5DF] rounded-2xl p-6 md:p-8 space-y-5"
          data-testid="passkey-manager">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <ScanFace size={18} className="text-[#C05746]"/>
            <Fingerprint size={18} className="text-[#C05746]"/>
            <h2 className="font-serif-display text-2xl">
              Sign in with Face ID / Touch ID
            </h2>
          </div>
          <p className="text-sm text-[#5C625E]">
            Add a passkey to this account and next time you sign in,
            just tap your finger or look at your camera — no password,
            no magic-link email round-trip.
          </p>
        </div>
      </div>

      {!loading && rows.length > 0 && (
        <ul className="border border-[#E8E5DF] rounded-xl divide-y divide-[#E8E5DF]"
             data-testid="passkey-list">
          {rows.map((p) => (
            <li key={p.credential_id}
                className="flex items-center justify-between px-4 py-3">
              <div>
                <p className="text-sm font-medium text-[#121614]">
                  <KeyRound size={14} className="inline mr-1"/>
                  Passkey ({p.aaguid?.slice(0, 8) || "device"})
                </p>
                <p className="text-xs text-[#5C625E]">
                  Added {prettyDate(p.created_at)}
                  {p.last_used_at && ` · last used ${prettyDate(p.last_used_at)}`}
                </p>
              </div>
              <button
                onClick={() => remove(p.credential_id)}
                data-testid={`passkey-remove-${p.credential_id.slice(0, 8)}`}
                className="text-xs text-[#C05746] hover:underline flex items-center gap-1"
              >
                <Trash2 size={12}/> Remove
              </button>
            </li>
          ))}
        </ul>
      )}

      <PasskeyRegisterButton onRegistered={load} />
      {rows.length === 0 && !loading && (
        <p className="text-xs text-[#5C625E]">
          No passkeys yet. Click above and follow your device&apos;s
          prompt to enrol Face ID / Touch ID / Windows Hello.
        </p>
      )}
    </div>
  );
}
